import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, ClipboardCheck, Search, Send, UserCheck, Wallet, type LucideIcon } from 'lucide-react';
import useFunderCoverage from '@/hooks/useFunderCoverage';
import { contactHref } from './siteFacts';

/**
 * Find the funders you are not billing yet — Funding & Growth (TRUS-776).
 *
 * The funder-coverage story is about the funders an organization already
 * bills. This is the other half, and the part that grows the organization:
 * the same capabilities that get one funder paid qualify it for others. The
 * loop is the in-app Funding & Growth settings, step for step, and each step
 * says who acts — agents draft, a person approves, nothing is sent unseen.
 *
 * Source counts are read from /api/public/funder-coverage and shown only when
 * the platform returns them; the section never shows a number it did not read.
 */

interface Step {
  icon: LucideIcon;
  title: string;
  body: string;
  who: 'agent' | 'person';
}

const STEPS: Step[] = [
  {
    icon: ClipboardCheck,
    title: 'Map what you deliver',
    body: 'Your provider and program capabilities are drawn from the programs and service codes you already run. Your team confirms or corrects each one.',
    who: 'person',
  },
  {
    icon: Search,
    title: 'Scan every source',
    body: 'Health plans that operate where you do, public funding programs and discovered sponsors, matched to what you deliver and where.',
    who: 'agent',
  },
  {
    icon: Wallet,
    title: 'See existing and new funders side by side',
    body: 'Funders you already bill, and funders the same services qualify you for — each with the reasons it matched and any deadline.',
    who: 'agent',
  },
  {
    icon: Bot,
    title: 'An agent drafts the outreach',
    body: 'An enrollment or partnership note written from your confirmed capabilities. It never contains member information.',
    who: 'agent',
  },
  {
    icon: UserCheck,
    title: 'A person approves and sends',
    body: 'Nothing goes out until someone on your team edits it, approves it and presses send. Every message is logged.',
    who: 'person',
  },
  {
    icon: Send,
    title: 'The next visit bills one more funder',
    body: 'A funder you win joins your funder book, and the encounters you already capture are attributed to it too.',
    who: 'agent',
  },
];

const n = (v: number) => v.toLocaleString();

const FunderDiscovery: React.FC<{ tone?: 'light' | 'muted' }> = ({ tone = 'muted' }) => {
  const { coverage } = useFunderCoverage();
  const sources = coverage.opportunity_sources;
  const payers = coverage.directory.payers;
  const counts = [
    payers > 0 ? `${n(payers)} payers in the national directory` : null,
    sources && sources.programs > 0 ? `${n(sources.programs)} public funding programs` : null,
    sources && sources.sponsors > 0 ? `${n(sources.sponsors)} discovered sponsors` : null,
  ].filter(Boolean) as string[];

  return (
    <section
      id="find-funders"
      className={`py-16 ${tone === 'muted' ? 'bg-gray-50 dark:bg-gray-900 border-y border-gray-200 dark:border-gray-800' : 'bg-white dark:bg-gray-950'}`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-sm font-semibold tracking-widest text-v2accent uppercase mb-2">Funding &amp; Growth</p>
        <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3">
          Find the funders you are not billing yet.
        </h2>
        <p className="text-gray-600 dark:text-gray-300 max-w-3xl mb-3">
          The work that gets you paid by one funder usually qualifies you for others. WeaveIn maps what your organization
          delivers, finds the plans, counties and grant programs that pay for it, and helps you reach them.
        </p>
        {counts.length > 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">Scanning today: {counts.join(' · ')}.</p>
        )}
        <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {STEPS.map((s, i) => (
            <li key={s.title} className="rounded-xl bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="w-9 h-9 rounded-lg bg-v2accent-soft flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-v2accent" aria-hidden="true" />
                </span>
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
 s.who === 'person'
 ? 'bg-v2accent-soft text-ink border-v2border'
 : 'bg-v2accent-soft text-v2accent border-v2accent'
 }`}
                >
                  {s.who === 'person' ? 'Person decides' : 'Agent drafts'}
                </span>
              </div>
              <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">
                <span className="text-gray-400 mr-1.5">{i + 1}.</span>
                {s.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">{s.body}</p>
            </li>
          ))}
        </ol>
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
          <Link
            to={contactHref('deliver', 'Funding & Growth')}
            className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-ink text-paper font-semibold hover:bg-ink-700 transition-colors"
          >
            See which funders you qualify for
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Turned on per organization. Your capabilities, opportunities and outreach are visible only to your organization.
          </p>
        </div>
      </div>
    </section>
  );
};

export default FunderDiscovery;
